import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Grid, Button } from '@curves/ui-base';
import useDeviceOrderApi from "@Apis/useDeviceOrderApi";

import SportsEquipmentLoading from "@Components/loading/SportsEquipmentLoading";
import { getRandomInt, localUser } from "@Util/utils";
import { FOOTER_TYPE, HEADER_TYPE, MEMBER_SITE_LOGON_TYPE } from "@Constants/dataEnums";
import { useMainLayoutContext } from "@Local/Components/Layout/Context/MainLaydoutContext";
import {
    PHASE,
    REDIRECT_NOTICE_MS,
    REDIRECT_TIMEOUT_MS,
    resolvePhaseOnDisplayTimeout,
    resolvePhaseOnPageShow,
    resolveRedirectPlan,
} from "./smartFitnessBandFlow";

/**
 * 智能服務轉導頁。
 * 流程：讀取服務資訊 →（畫面完整顯示後）提示即將前往 → 自動轉導宇康頁；
 * 自動轉導若未生效，改顯示手動前往按鈕，避免使用者卡在不動的畫面。
 */
const SmartFitnessBandPage = () => {

    const [phase, setPhase] = useState(PHASE.LOADING);
    const [pageUrl, setPageUrl] = useState('');
    const { getMemberDeviceOrderApi, getEmployeeDeviceOrderApi } = useDeviceOrderApi();
    const { setHeaderType, setFooterType } = useMainLayoutContext();

    const { logonType } = localUser.get() || {};

    // 本頁是否已真的被切走：用來排除「其實轉導成功了」卻被判定成失敗
    const hasLeftRef = useRef(false);
    // 是否已送出過自動轉導：bfcache 還原時據此判斷該不該切到手動模式
    const hasRedirectedRef = useRef(false);
    // 固定運動動畫的圖組，避免切換提示文字造成重繪時圖案跟著跳換
    const spritesIndexRef = useRef(getRandomInt(3) + 1);

    useEffect(
        () => {
            let isMounted = true;
            let displayTimer = null;

            setHeaderType(HEADER_TYPE.HIDE_USER | HEADER_TYPE.LOGO_CENTER | HEADER_TYPE.HIDE_BACKGROUND);
            setFooterType(FOOTER_TYPE.FLEX | FOOTER_TYPE.HIDE_BORDER_TOP);

            // 真的離開本頁時留記號，避免轉導成功仍被判定為失敗
            const handlePageHide = () => {
                hasLeftRef.current = true;
            };

            /**
             * bfcache 還原（多半是從宇康頁按返回）時改走手動模式，詳見 resolvePhaseOnPageShow。
             * @param {PageTransitionEvent} event
             */
            const handlePageShow = (event) => {
                if (!isMounted) return;
                hasLeftRef.current = false;
                setPhase((prev) => resolvePhaseOnPageShow(prev, event.persisted, hasRedirectedRef.current));
            };

            window.addEventListener('pagehide', handlePageHide);
            window.addEventListener('pageshow', handlePageShow);

            (async () => {
                const resp =
                    logonType === MEMBER_SITE_LOGON_TYPE.MEMBER
                        ? await getMemberDeviceOrderApi()
                        : logonType === MEMBER_SITE_LOGON_TYPE.EMPLOYEE
                            ? await getEmployeeDeviceOrderApi() : null;

                if (!isMounted) return;

                const { phase: nextPhase, pageUrl: url, waitMs } = resolveRedirectPlan(resp);

                if (nextPhase === PHASE.UNBOUND) {
                    setPhase(PHASE.UNBOUND);
                    return;
                }

                setPageUrl(url);

                // 先把畫面完整顯示出來再轉導；delayable 時 waitMs 會改成宇康建檔所需的等待時間。
                // 計時期間階段可能已被改掉（bfcache 還原），故交給 resolvePhaseOnDisplayTimeout 判斷能否推進
                displayTimer = setTimeout(
                    () => {
                        if (!isMounted) return;
                        setPhase((prev) => resolvePhaseOnDisplayTimeout(prev, nextPhase));
                    },
                    waitMs,
                );
            })();

            return () => {
                isMounted = false;
                if (displayTimer) clearTimeout(displayTimer);
                window.removeEventListener('pagehide', handlePageHide);
                window.removeEventListener('pageshow', handlePageShow);
            };
        },
        // eslint-disable-next-line
        [],
    );

    /**
     * 送出轉導，並偵測是否真的跳走。
     * 瀏覽器沒有「導航被攔截」的事件，故以反證法判定：轉導若成功，document 會被卸載，
     * 下面的 fallbackTimer 就不會有機會執行；時間到仍留在本頁，即視為自動轉導失效。
     */
    useEffect(
        () => {
            if (phase !== PHASE.REDIRECTING || !pageUrl) return undefined;

            let fallbackTimer = null;

            // 讓「即將前往」的提示先畫出來，再真的跳走
            const launchTimer = setTimeout(() => {
                hasRedirectedRef.current = true;
                window.location.href = pageUrl;

                fallbackTimer = setTimeout(() => {
                    if (hasLeftRef.current) return;
                    setPhase(PHASE.FALLBACK);
                }, REDIRECT_TIMEOUT_MS);
            }, REDIRECT_NOTICE_MS);

            return () => {
                clearTimeout(launchTimer);
                if (fallbackTimer) clearTimeout(fallbackTimer);
            };
        },
        [phase, pageUrl],
    );

    const renderContent = () => {
        if (phase === PHASE.UNBOUND) {
            return (
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Typography className="text-center" variant="h3" color="white">
                            尚未啟用智能服務
                        </Typography>
                        <Typography className="text-center" variant="h4" color="white">
                            若有疑問請尋求教練協助排解
                        </Typography>
                    </Grid>
                </Grid>
            );
        }

        if (phase === PHASE.FALLBACK) {
            return (
                <Box className="container smart-fitness-band-fallback">
                    <Typography variant="h4" color="white">
                        尚未自動前往智能服務
                    </Typography>
                    <Typography variant="body1" color="white">
                        請點擊下方按鈕繼續
                    </Typography>
                    {/* 刻意用原生連結（ui-base Button 帶 href 會渲染成 a）：
                        由使用者手勢觸發的導航不會被 LINE in-app browser 攔下 */}
                    <Button variant="contained" fullWidth href={pageUrl}>
                        前往智能服務
                    </Button>
                </Box>
            );
        }

        return (
            <Box className="container">
                <SportsEquipmentLoading
                    size={1}
                    spritesIndex={spritesIndexRef.current}
                    text={phase === PHASE.LOADING ? '讀取智能服務中' : '即將前往智能服務'}
                />
            </Box>
        );
    };

    return (
        <Box className="page-linear-purple smart-fitness-band-page">
            {renderContent()}
        </Box>
    );
}

export default SmartFitnessBandPage;
